import { useId, useState } from "react";
import { Input } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import classNames from "classnames";
import { IconLoading } from "@icons/index";
import { isDevelopment } from "@utils/env";
import { useLinkAutoParse } from "@hooks/useLinkAutoParse";

export interface CharacterLinkInputStepProps {
  parsing: boolean;
  error: string | null;
  /** 解析链接成功后由父组件负责把封面写入主图槽并跳到 editor。aborted 时父组件应跳过所有副作用。 */
  onParse: (url: string, signal: AbortSignal) => Promise<boolean>;
}

/**
 * Create Character Modal 的视频链接输入态。
 *
 * 设计稿节点：3536:23389
 *
 * 输入框前缀固定 `https://`，placeholder 提示支持 TikTok / Kalodata / YouTube Shorts。
 * 输入即自动解析（debounce 450ms），按 Enter 立即触发；switchMap 保证以最后一次输入为准。
 * 解析失败仅展示红色提示，不像 Product 那样给"切到手动"兜底——Character 的 manual 模式
 * 由外层下拉直接选择，不在解析失败页内提供二次入口。
 */
export const CharacterLinkInputStep = ({ parsing, error, onParse }: CharacterLinkInputStepProps) => {
  const [url, setUrl] = useState(
    isDevelopment() ? "https://www.tiktok.com/@morganliving/video/7632118005249445134" : "",
  );
  const id = useId();
  const { submit, submitImmediately } = useLinkAutoParse(onParse);

  return (
    <div className="pt-[8px] flex flex-col gap-[8px]">
      <div
        className={classNames(
          "flex items-center rounded-[8px] bg-kds-bg-element px-[12px] h-[56px] border border-solid",
          error ? "border-kds-danger-border" : "border-kds-border-subtle",
        )}
      >
        <span className="text-[16px] text-kds-fg font-semibold shrink-0 leading-[24px]">https://</span>
        <span className="shrink-0 w-px h-[48px] bg-kds-border-solid mx-[8px]" />
        <Input
          id={id}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            submit(e.target.value);
          }}
          onPressEnter={() => submitImmediately(url)}
          variant="borderless"
          placeholder={t("Paste a TikTok, Kalodata, or YouTube Shorts video link.")}
          className="flex-1 bg-transparent! px-0! h-[24px] text-[14px] text-kds-fg placeholder:text-kds-fg-subtle"
          autoFocus
        />
        {parsing && <IconLoading className="w-[16px] h-[16px] animate-spin text-kds-fg-soft ml-[8px]" />}
      </div>
      {error && (
        <div className="text-[12px] leading-[16px] text-kds-danger-fg">
          {"* "}
          <Trans>Currently supports links from TikTok, Kalodata, and YouTube Shorts.</Trans>
        </div>
      )}
    </div>
  );
};
